import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import Modal from '@rd-web-markets/shared/dist/util/Modal';
import TextInputGroup from '@components/util/TextInputGroup';
import { DatePicker } from '@rd-web-markets/shared/dist/util/date';
import AlertList from '@rd-web-markets/shared/dist/util/AlertList';
import { generateClaimDates } from './generateClaimDates';
import { Button } from '../util/buttons/Button';
import { DeleteButton } from '../util/buttons/DeleteButton';
import { createTimezonedDate } from '../util/date/dateUtils';
export default function NewClaimListingModal(_ref) {
  let {
    show,
    onCancel,
    onHide,
    onSubmit,
    singleOrMultiYearClaim,
    existingClaims = []
  } = _ref;
  // Sometimes the date string may come in a format that is easy to create a date from it
  // but not easy to make it into a datetimestring.
  const createDateAt0Hours = dateString => {
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
  };
  const listExistingClaimsWithFixedDates = existingClaims => {
    return existingClaims.map(claim => {
      return {
        to_date: createTimezonedDate(claim.to_date).toCustomLocaleTimeString(),
        from_date: createTimezonedDate(claim.from_date).toCustomLocaleTimeString(),
        isAlreadyExisting: true
      };
    });
  };
  const [claimPeriodsValidationAlerts, setClaimPeriodsValidationAlerts] = useState([]);
  const [existingClaimDates, setExistingClaimDates] = useState(listExistingClaimsWithFixedDates(existingClaims));
  const currentDate = new Date();
  const [accountingPeriod, setAccountingPeriod] = useState({
    from: singleOrMultiYearClaim.from_date || new Date(currentDate.getFullYear(), 0, 1).toCustomLocaleTimeString(),
    to: singleOrMultiYearClaim.to_date || new Date(currentDate.getFullYear(), 11, 31).toCustomLocaleTimeString()
  });
  const [newClaims, setNewClaims] = useState(null);
  const validateClaimDates = useCallback(() => {
    const previousClaims = [];
    const newValidationMessages = new Set();
    newClaims && newClaims.forEach(claim => {
      if (claim.from_date < accountingPeriod.from || claim.to_date < accountingPeriod.from) {
        newValidationMessages.add('Claim date cannot be set earlier than the accounting period start date.');
      }
      if (claim.from_date > accountingPeriod.to || claim.to_date > accountingPeriod.to) {
        newValidationMessages.add('Claim date cannot be set later than the accounting period end date.');
      }
      if (claim.from_date === accountingPeriod.to) {
        newValidationMessages.add('Claim cannot start on the accounting period end date.');
      }
      if (claim.from_date >= claim.to_date) {
        newValidationMessages.add('Claim start date cannot be equal or later than that claim\'s end date.');
      }
      if (process.env.PUBLIC_URL === '/us' && claim.to_date.getFullYear() - claim.from_date.getFullYear() !== 0) {
        newValidationMessages.add('Year gap should not be more than one year');
      }
      [...existingClaimDates, ...previousClaims].forEach(previousClaim => {
        if (claim.from_date >= previousClaim.from_date && claim.from_date <= previousClaim.to_date || claim.to_date >= previousClaim.from_date && claim.to_date <= previousClaim.to_date) {
          let message = 'Claim periods cannot overlap.';
          if (previousClaim.isAlreadyExisting || claim.isAlreadyExisting) {
            message = 'Existing claim: ' + message;
          }
          newValidationMessages.add(message);
        }
        if (claim.to_date.getFullYear() === previousClaim.to_date.getFullYear()) {
          let message = 'Claim periods cannot overlap.';
          if (previousClaim.isAlreadyExisting || claim.isAlreadyExisting) {
            message = 'Existing claim: ' + message;
          }
          newValidationMessages.add(message);
        }
      });
      previousClaims.push(claim);
    });
    setClaimPeriodsValidationAlerts([...newValidationMessages].map(m => ({
      message: m,
      id: Math.random()
    })));
  }, [accountingPeriod.from, accountingPeriod.to, existingClaimDates, newClaims]);
  const addNewClaim = () => {
    const lastClaim = newClaims[newClaims.length - 1] || {
      to_date: accountingPeriod.to
    };
    const year = lastClaim.to_date.getFullYear();
    let newClaim = null;
    if (lastClaim.to_date.getMonth() + 1 === 3) {
      newClaim = {
        from_date: createDateAt0Hours(`${year}-4-1`),
        to_date: createDateAt0Hours(`${year + 1}-3-31`)
      };
    } else {
      newClaim = {
        from_date: createDateAt0Hours(`${year + 1}-1-1`),
        to_date: createDateAt0Hours(`${year + 1}-12-31`)
      };
    }
    newClaims.push(newClaim);
    setNewClaims([...newClaims]);
  };

  // if the singleOrMultiYearClaim is a multi-year claim we convert it to several single year claims
  useEffect(() => {
    const newClaims = generateClaimDates(accountingPeriod);
    setNewClaims(newClaims);
  }, [accountingPeriod]);
  useEffect(() => {
    validateClaimDates();
  }, [newClaims, validateClaimDates]);
  const selectedClaims = () => {
    return newClaims && newClaims.length > 0;
  };
  const singleYearClaimRow = (claimDates, index) => {
    return /*#__PURE__*/React.createElement(Row, {
      className: "mt-3",
      key: index
    }, /*#__PURE__*/React.createElement(Col, {
      md: 2
    }, "Claim ", claimDates.to_date.getFullYear()), /*#__PURE__*/React.createElement(Col, {
      md: 5
    }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
      md: 3
    }, "From:"), /*#__PURE__*/React.createElement(Col, {
      md: 9
    }, /*#__PURE__*/React.createElement(TextInputGroup, {
      placeholder: "Start Date",
      prependIcon: "calendar_month"
    }, /*#__PURE__*/React.createElement(DatePicker, {
      className: "border-0",
      selected: claimDates.from_date,
      shouldFormatDate: false,
      onChange: date => {
        newClaims[index].from_date = date;
        setNewClaims([...newClaims]);
      }
    }))))), /*#__PURE__*/React.createElement(Col, {
      md: 4
    }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
      md: 3
    }, "To:"), /*#__PURE__*/React.createElement(Col, {
      md: 9
    }, /*#__PURE__*/React.createElement(TextInputGroup, {
      placeholder: "Start Date",
      prependIcon: "calendar_month"
    }, /*#__PURE__*/React.createElement(DatePicker, {
      className: "border-0",
      selected: claimDates.to_date,
      shouldFormatDate: false,
      onChange: date => {
        newClaims[index].to_date = date;
        setNewClaims([...newClaims]);
      }
    }))))), /*#__PURE__*/React.createElement(Col, {
      md: 1
    }, /*#__PURE__*/React.createElement(DeleteButton, {
      onClick: () => {
        newClaims.splice(index, 1);
        setNewClaims([...newClaims]);
      }
    })));
  };
  const newClaimListingModalBody = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, null, /*#__PURE__*/React.createElement(Card.Body, null, !selectedClaims() && /*#__PURE__*/React.createElement(Alert, {
      variant: "warning"
    }, "Please updated the Start Date to 01/01/YYYY and End Date to 31/12/YYYY"), /*#__PURE__*/React.createElement("h5", null, /*#__PURE__*/React.createElement("b", null, "Please select if the below claim is a single claim or spans multiple financial years:")), /*#__PURE__*/React.createElement(Row, {
      className: "mt-5"
    }, /*#__PURE__*/React.createElement(Col, {
      md: 6
    }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
      md: 5
    }, "Accounting Period", /*#__PURE__*/React.createElement("br", null), "Start Date"), /*#__PURE__*/React.createElement(Col, {
      md: 6
    }, /*#__PURE__*/React.createElement(TextInputGroup, {
      placeholder: "Start Date",
      prependIcon: "calendar_month"
    }, /*#__PURE__*/React.createElement(DatePicker, {
      className: "border-0",
      selected: accountingPeriod.from,
      shouldFormatDate: false,
      onChange: date => {
        setAccountingPeriod({
          ...accountingPeriod,
          from: date
        });
      }
    }))))), /*#__PURE__*/React.createElement(Col, {
      md: 6
    }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
      md: 5
    }, "Accounting Period", /*#__PURE__*/React.createElement("br", null), "End Date"), /*#__PURE__*/React.createElement(Col, {
      md: 6
    }, /*#__PURE__*/React.createElement(TextInputGroup, {
      placeholder: "Start Date",
      prependIcon: "calendar_month"
    }, /*#__PURE__*/React.createElement(DatePicker, {
      className: "border-0",
      selected: accountingPeriod.to,
      shouldFormatDate: false,
      onChange: date => {
        setAccountingPeriod({
          ...accountingPeriod,
          to: date
        });
      }
    })))))), /*#__PURE__*/React.createElement(Row, {
      className: "mt-5 border-top"
    }, !!claimPeriodsValidationAlerts.length && /*#__PURE__*/React.createElement(Col, {
      md: 12,
      className: "mt-3 mb-3"
    }, /*#__PURE__*/React.createElement(Col, {
      md: {
        span: 6,
        offset: 3
      }
    }, /*#__PURE__*/React.createElement(AlertList, {
      alerts: claimPeriodsValidationAlerts,
      onRemoveAlert: id => setClaimPeriodsValidationAlerts(claimPeriodsValidationAlerts.filter(alert => alert.id !== id))
    }))), /*#__PURE__*/React.createElement(Col, {
      md: 12
    }, newClaims && newClaims.map((claim, index) => singleYearClaimRow(claim, index)))), /*#__PURE__*/React.createElement(Row, {
      className: "mt-3"
    }, /*#__PURE__*/React.createElement(Col, {
      md: 4
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "primary",
      onClick: addNewClaim,
      icon: "add",
      iconPosition: "right"
    }, "Add Claim")))), /*#__PURE__*/React.createElement(Card.Footer, null)));
  };
  return /*#__PURE__*/React.createElement(Modal, {
    size: "lg",
    title: "New Claim Listing",
    show: show,
    renderBody: newClaimListingModalBody,
    disabled: !!claimPeriodsValidationAlerts.length,
    onCancel: onCancel,
    onHide: onHide,
    onSubmit: !selectedClaims() ? null : () => {
      onSubmit(newClaims);
    }
  });
}